import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Box, Chip, TableCell, TableRow, Typography } from '@mui/material';
import { PauseLogEntry } from 'models/workflow-step';
import TimeInStep from 'components/shared/time-in-step/time-in-step.component';

const getElapsedTime = (from: string | Date, to?: string | Date) => {
    const elapsed = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();
    return {
        hours: Math.floor(elapsed / 3600000),
        minutes: Math.floor((elapsed % 3600000) / 60000),
        seconds: Math.floor((elapsed % 60000) / 1000),
    };
};

const getIndividualPauseDuration = (entry: PauseLogEntry, pauseLog: PauseLogEntry[], index: number) => {
    if (entry.type !== 'pause') return undefined;
    const followingResume = pauseLog.slice(index + 1).find((e) => e.type === 'resume');
    return getElapsedTime(entry.dateTime, followingResume?.dateTime);
};

type PauseLogRowProps = { entry: PauseLogEntry; pauseLog: PauseLogEntry[]; index: number };

const PauseLogRow: FC<PauseLogRowProps> = ({ entry, pauseLog, index }) => {
    const { t: translate } = useTranslation(['workflow']);
    const duration = getIndividualPauseDuration(entry, pauseLog, index);

    return (
        <TableRow>
            <TableCell sx={{ maxWidth: '200px' }}>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" color="textSecondary">
                        {entry.type === 'pause' ? translate('workflow:PAUSE') : translate('workflow:RESUME')}
                    </Typography>
                    {entry.type === 'pause' && entry.reason && (
                        <Chip
                            size="small"
                            color="warning"
                            label={entry.reason}
                            title={entry.reason}
                            sx={{ maxWidth: '100%' }}
                            data-test="pause-log-row.reason-chip"
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ width: '150px' }}>{formatDate(new Date(entry.dateTime), 'datetime')}</TableCell>
            <TableCell />
            <TableCell>{entry.createdBy?.fullName}</TableCell>
            <TableCell>{entry.comment}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell>{duration && <TimeInStep timeSpent={duration} maxHourInStep={0} />}</TableCell>
        </TableRow>
    );
};

export default PauseLogRow;
